import {useEffect, useMemo, useRef, useState} from 'react';
import {useDebounce} from 'react-use';
import {Alert, AlertTitle, Button, Popover, TextField} from '@ezcater/tapas';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import poweredByGoogleImage from '@/assets/images/powered_by_google_on_white_hdpi.png';
import {useGlobalFulfillmentDetail} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import DeliveryPickupToggle, {
  FULFILLMENT_OPTIONS,
  fulfillmentOptionId,
  fulfillmentRadiogroupId,
} from '@/components/global/shared/DeliveryPickupToggle';
import {FulfillmentDetailStrategy} from '@/graphql/types';
import useGoogleAutocomplete from '@/hooks/useGoogleAutocomplete';
import useIdentity from '@/hooks/useIdentity';
import usePreciseAddressNudge from '@/hooks/usePreciseAddressNudge';
import useSavedAddressSearch from '@/hooks/useSavedAddressSearch';
import useUserGeolocationToAddress from '@/hooks/useUserGeolocationToAddress';
import {
  fulfillmentDetailAddressInputToString,
  geocodedAddressResultToString,
} from '@/utils/geocodedAddressResult';
import {type Address} from '@/utils/googleAutocomplete';
import EventBarSearchHistoryOption from '../EventBarSearchHistoryOption';
import {useEventBarV2, type WhereSelection} from '../EventBarV2Provider';
import {useCommitEventBarFulfillment} from '../hooks/useCommitEventBarFulfillment';
import {useEventBarFallbackAddress} from '../hooks/useEventBarFallbackAddress';
import useEventBarSearchHistory, {
  type EventBarSearchHistoryEntry,
} from '../hooks/useEventBarSearchHistory';
import {useEventBarV2Tracking} from '../hooks/useEventBarV2Tracking';
import {useWhereAddressSelection} from '../hooks/useWhereAddressSelection';
import InfoBanner from '../InfoBanner';
import {
  getRecentlyUsedAddresses,
  type RecentAddress,
  recentAddressToDisplayAddress,
  RECENTLY_USED_ADDRESS_LIMIT,
} from '../recentlyUsedAddresses';
import {focusAdjacentTabbable} from '../utils/fields';
import {
  isPreciseAddress,
  isPreciseWhereSelection,
  searchHistoryAddressToGeocoded,
} from '../utils/where';
import UseCurrentLocationRow from './CurrentLocationRow';
import WhereAddressOption from './WhereAddressOption';
import WhereCorpAccountSelect from './WhereCorpAccountSelect';

// The result of the pre-search Where validation. When the guard had to resolve a typed-but-unselected
// address, it hands the resolved selection back so the caller can commit it in the same tick, before
// the `setWhereSelection` state update renders.
export type SearchGuardResult = {ok: boolean; resolvedSelection?: WhereSelection};

type WhereProps = {
  isCompressed?: boolean;
  isOpen: boolean;
  // Hands the bar on to the next field that still needs asking about, if any. A no-op on menu
  // pages, which edit in place behind the panel's own Update button.
  onAdvance: () => void;
  onUpdateApplied?: () => void;
  registerSearchGuard: (guard: () => Promise<SearchGuardResult>) => void;
  setIsOpen: (open: boolean) => void;
};

type WhereOption =
  | {kind: 'geo'}
  | {address: Address; kind: 'address'}
  | {address: Address; kind: 'recent'; recent: RecentAddress}
  | {entry: EventBarSearchHistoryEntry; kind: 'history'};

const optionId = (index: number, idSuffix: string) => `where-option-${index}-${idSuffix}`;

// Flat nav index 0 is the Delivery/Pickup toggle; index 1 is "use current location".
const GEO_OPTION_INDEX = 1;

type PopoverOpenChange = NonNullable<React.ComponentProps<typeof Popover.Root>['onOpenChange']>;

const Where: React.FC<WhereProps> = ({
  isCompressed = false,
  isOpen,
  onAdvance,
  onUpdateApplied,
  setIsOpen,
  registerSearchGuard,
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasEditedSinceFocus, setHasEditedSinceFocus] = useState(false);

  const {t} = useTranslation('app-bar');
  const {
    whereValue,
    setWhereValue,
    whereSelection,
    setWhereSelection,
    fulfillmentType,
    setFulfillmentType,
    hasUserEditedWhereRef,
    resetWhereAddressToFulfillmentDetail,
    whereResolutionSeqRef,
    hasUnresolvedWhereAddress,
    setHasUnresolvedWhereAddress,
  } = useEventBarV2();
  const {trackOrderType, trackSearchHistorySelected, trackWhereUpdate} = useEventBarV2Tracking();

  const {isOnMenuPage} = useStoreNextRouter();
  const {fulfillmentDetail} = useGlobalFulfillmentDetail();
  const fallbackAddress = useEventBarFallbackAddress();
  const fallbackAddressString = fallbackAddress
    ? fulfillmentDetailAddressInputToString(fallbackAddress)
    : '';
  const {commitFulfillment} = useCommitEventBarFulfillment();

  const {
    incompleteAddress,
    addressesToVerify,
    clearValidation,
    flagIncompleteAddress,
    selectGeocoded,
    selectSavedAddress,
    selectRecentAddress,
    selectSearchHistoryAddress,
    selectGeolocation,
    selectTypedAddress,
    resolveTypedAddressForSearch,
  } = useWhereAddressSelection({onSelected: onAdvance});

  const handleFulfillmentChange = (value: (typeof FULFILLMENT_OPTIONS)[number]) => {
    if (value !== fulfillmentType) trackOrderType(value);
    setFulfillmentType(value);
  };

  // Validate the address before a search runs. Where is required, so an empty address blocks the
  // search and surfaces the "complete address" banner; When and Who stay optional. When a typed
  // address has to be resolved here, hand the selection back so the caller commits it directly
  // rather than reading `whereSelection` before its state update has rendered.
  const runSearchGuard = async (): Promise<SearchGuardResult> => {
    if (incompleteAddress || addressesToVerify) return {ok: false};
    if (!whereValue.trim()) {
      flagIncompleteAddress();
      return {ok: false};
    }
    // On pages other than the menu pages, a detail-less commit *creates* the detail, and the selection is the only
    // address it gets — so resolve the one already in the field instead of letting it through
    // untouched, which would bail the create out and drop the staged When/Who values with it.
    const commitKeepsAddress = Boolean(fulfillmentDetail) || isOnMenuPage;
    if (whereSelection || (!hasUserEditedWhereRef.current && commitKeepsAddress)) return {ok: true};
    // The field shows the fallback's text, so commit the fallback itself rather than geocoding it
    // again.
    if (fallbackAddress && whereValue === fallbackAddressString)
      return {ok: true, resolvedSelection: {kind: 'fallback', address: fallbackAddress}};
    const resolvedSelection = await resolveTypedAddressForSearch(whereValue);
    return resolvedSelection ? {ok: true, resolvedSelection} : {ok: false};
  };

  // Re-register every render so the parent's guard always closes over the latest field state.
  useEffect(() => {
    registerSearchGuard(runSearchGuard);
  });

  // Menu-page Update: validate the address (reusing the search guard so incomplete/multiple-match
  // states surface in-panel), commit the fulfillment detail, then close. A failed guard keeps the
  // panel open showing the error.
  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      // The guard geocodes a typed-but-unselected address, so keep the button loading across it too;
      // the finally resets when the guard fails and the panel stays open showing the error.
      const guard = await runSearchGuard();
      // Track after the guard so an address it resolved is reported as the one about to be
      // committed, but outside the ok check so a click that fails validation is still recorded —
      // there the selection is undefined, which honestly reports the still-committed address.
      trackWhereUpdate(guard.resolvedSelection);
      if (!guard.ok) return;
      // Keep the panel open on a failed commit so the pending edit isn't silently lost.
      if ((await commitFulfillment(guard.resolvedSelection)).ok) {
        setIsOpen(false);
        onUpdateApplied?.();
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const selectOnMouseUpRef = useRef(false);
  const corpSelectTriggerRef = useRef<HTMLButtonElement>(null);
  const anchorRef = useRef<HTMLLabelElement>(null);
  const idSuffix = isCompressed ? 'compressed' : 'expanded';
  const containerId = `where-container-${idSuffix}`;
  const inputId = `where-input-${idSuffix}`;
  const listboxId = `where-suggestions-listbox-${idSuffix}`;
  const {data: identityData} = useIdentity();
  const corpAccountId = identityData?.me?.consumerAccount?.corporateAccount?.id ?? undefined;
  const isLoggedIn = !!identityData?.me?.consumerAccount;
  const hasMultipleCorpAccounts =
    (identityData?.me?.consumerAccount?.corporateAccounts?.length ?? 0) > 1;
  const {
    clearUserGeolocationResults,
    handleGeolocationClick,
    isLoading,
    showUserGeolocatedError,
    userGeolocatedAddress,
    userGeolocatedAddressString,
  } = useUserGeolocationToAddress();

  const defaultWhereValue = fulfillmentDetail?.address?.fullAddress || fallbackAddressString;

  const {showRedDot, showTextPrompt} = usePreciseAddressNudge();
  const [lastWhereSelection, setLastWhereSelection] = useState(whereSelection);
  if (whereSelection && whereSelection !== lastWhereSelection) {
    setLastWhereSelection(whereSelection);
  }
  const nudgeSelection = whereValue === defaultWhereValue ? whereSelection : lastWhereSelection;

  const hasPreciseAddress =
    !hasUnresolvedWhereAddress &&
    (nudgeSelection
      ? isPreciseWhereSelection(nudgeSelection)
      : isPreciseAddress(fulfillmentDetail?.address));
  const showPreciseAddressRedDot = showRedDot && !hasPreciseAddress;
  const showPreciseAddressTextPrompt = showTextPrompt && !hasPreciseAddress && !isOpen;
  const showPreciseAddressNudge = showRedDot || showTextPrompt;
  const textPromptId = `where-text-prompt-${idSuffix}`;
  const placeholder =
    fulfillmentType === FulfillmentDetailStrategy.Delivery
      ? t('eventBarV2.where.placeholderDelivery')
      : t('eventBarV2.where.placeholderPickup');
  const redDot = (
    <span
      aria-hidden
      className="relative mt-0.5 size-2 shrink-0 rounded-full bg-red-600"
      data-testid="where-precise-address-red-dot"
    >
      <span className="absolute inset-0 animate-[ping_1s_cubic-bezier(0,0,0.2,1)_2_forwards] rounded-full bg-red-600 motion-reduce:animate-none" />
    </span>
  );

  useEffect(() => {
    if (!hasUserEditedWhereRef.current && defaultWhereValue) {
      setWhereValue(defaultWhereValue);
    }
  }, [defaultWhereValue, hasUserEditedWhereRef, setWhereValue]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Filter on an empty query until the value is edited.
  //  Each new focus starts over from that default.
  const searchQuery = hasEditedSinceFocus ? whereValue : '';

  useDebounce(() => setDebouncedValue(searchQuery), 200, [searchQuery]);

  const predictions = useGoogleAutocomplete(isOpen ? debouncedValue : '');
  const {addresses: savedAddresses} = useSavedAddressSearch({
    corpAccountId,
    limit: 3,
    // A single space allows the saved-address query to run when the input is empty
    searchTerm: searchQuery || ' ',
    skip: !isLoggedIn || !isOpen,
  });

  const showSuggestions = searchQuery.length > 0;
  const visiblePredictions = useMemo(
    () => (showSuggestions ? predictions : []),
    [showSuggestions, predictions],
  );

  const recentAddresses = useMemo<RecentAddress[]>(
    () => (!isLoggedIn && isOpen ? getRecentlyUsedAddresses() : []),
    [isLoggedIn, isOpen],
  );

  // Filtered by the typed value like saved addresses (empty input shows all).
  const filteredRecentAddresses = useMemo<RecentAddress[]>(() => {
    const query = searchQuery.trim().toLowerCase();
    const matches = query
      ? recentAddresses.filter(recent =>
          `${recent.mainText} ${recent.secondaryText}`.toLowerCase().includes(query),
        )
      : recentAddresses;
    return matches.slice(0, RECENTLY_USED_ADDRESS_LIMIT);
  }, [searchQuery, recentAddresses]);
  const showRecentAddresses = filteredRecentAddresses.length > 0;

  // Logged-in counterpart to the recently-used addresses above.
  const allSearchHistory = useEventBarSearchHistory();
  const searchHistory = useMemo<EventBarSearchHistoryEntry[]>(() => {
    if (!isLoggedIn || !isOpen) return [];
    // Match on the address itself, not the City, ST label every search in a city shares.
    const query = searchQuery.trim().toLowerCase();
    const matches = query
      ? allSearchHistory.filter(entry =>
          (entry.address.fullAddress ?? entry.addressLabel).toLowerCase().includes(query),
        )
      : allSearchHistory;
    // Don't repeat an address that's already offered under Saved addresses (matched by full address).
    const savedFullAddresses = new Set(
      savedAddresses.map(saved => saved.name.trim().toLowerCase()),
    );
    const withoutSaved = matches.filter(
      entry => !savedFullAddresses.has((entry.address.fullAddress ?? '').trim().toLowerCase()),
    );
    // Where only shows the street (no date/headcount), so collapse entries at the same street, city and
    // state — keeping the first (most recent) — to avoid visually-identical rows. The zip is left out:
    // the same street can geocode to different zips.
    const addressKey = ({address, addressLabel}: EventBarSearchHistoryEntry) =>
      (address.street
        ? [address.street, address.city, address.state].join('|')
        : (address.fullAddress ?? addressLabel)
      )
        .trim()
        .toLowerCase();
    const seenAddresses = new Set<string>();
    const deduped = withoutSaved.filter(entry => {
      const key = addressKey(entry);
      if (seenAddresses.has(key)) return false;
      seenAddresses.add(key);
      return true;
    });
    return deduped.slice(0, RECENTLY_USED_ADDRESS_LIMIT);
  }, [isLoggedIn, isOpen, searchQuery, allSearchHistory, savedAddresses]);
  const showSearchHistory = searchHistory.length > 0;

  // Flat, visually-ordered list of the selectable options for arrow-key navigation.
  const options = useMemo<WhereOption[]>(() => {
    const list: WhereOption[] = [{kind: 'geo'}]; // Current location is always shown.
    savedAddresses.forEach(address => list.push({address, kind: 'address'}));
    filteredRecentAddresses.forEach(recent =>
      list.push({address: recentAddressToDisplayAddress(recent), kind: 'recent', recent}),
    );
    searchHistory.forEach(entry => list.push({entry, kind: 'history'}));
    visiblePredictions.forEach(address => list.push({address, kind: 'address'}));
    return list;
  }, [savedAddresses, filteredRecentAddresses, searchHistory, visiblePredictions]);

  // Flat-index offsets for each rendered section.
  // Nav index 0 is the Delivery/Pickup toggle.
  // Current Location is index 1
  // The address options begin at index 2.
  const savedStart = GEO_OPTION_INDEX + 1;
  const recentStart = savedStart + savedAddresses.length;
  const searchHistoryStart = recentStart + filteredRecentAddresses.length;
  const predictionsStart = searchHistoryStart + searchHistory.length;
  const optionCount = 1 + options.length;
  const activeOptionIndex = activeIndex < optionCount ? activeIndex : -1;

  const activeDescendantId =
    activeOptionIndex === 0
      ? fulfillmentOptionId(fulfillmentType, idSuffix)
      : activeOptionIndex >= 1
        ? optionId(activeOptionIndex, idSuffix)
        : undefined;

  const triggerGeolocation = () => {
    if (!isLoading) handleGeolocationClick();
  };

  useEffect(() => {
    if (userGeolocatedAddress && userGeolocatedAddressString) {
      hasUserEditedWhereRef.current = true;
      setWhereValue(userGeolocatedAddressString);
      selectGeolocation(userGeolocatedAddress);
    }
    // selectGeolocation is stable per render; intentionally excluded to keep this keyed on the result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGeolocatedAddress, userGeolocatedAddressString, hasUserEditedWhereRef, setWhereValue]);

  // Runs for every close, whatever triggered it — Base UI's `onOpenChange` or the Tab handler
  // closing the panel itself. `resolveTypedAddress` dedupes concurrent calls, so it's safe if both
  // paths fire for one dismissal.
  const handleClose = () => {
    setActiveIndex(-1);
    // Only relevant when the user typed into Where but never landed on a real selection.
    if (!hasUserEditedWhereRef.current || whereSelection) return;
    if (!whereValue.trim()) {
      if (isOnMenuPage) resetWhereAddressToFulfillmentDetail();
      else setHasUnresolvedWhereAddress(true);
      return;
    }
    // Resolve the typed value so a valid address is staged for the next search. On a menu page,
    // if it can't be resolved to a real address, restore the previously committed address — unless
    // a pick or reset superseded the lookup meanwhile.
    const seq = whereResolutionSeqRef.current;
    void resolveTypedAddressForSearch(whereValue).then(resolved => {
      if (resolved || !isOnMenuPage || whereResolutionSeqRef.current !== seq) return;
      resetWhereAddressToFulfillmentDetail();
      clearValidation();
    });
  };

  const handleOpenChange: PopoverOpenChange = (next, eventDetails) => {
    // Presses inside the anchor container are part of the field itself, so they must not dismiss
    // the popup. Base UI replaces Radix's `onInteractOutside` with a cancelable `onOpenChange`.
    if (eventDetails.reason === 'outside-press') {
      const target = eventDetails.event.target as HTMLElement | null;
      // Keep the panel open for interactions inside the Where field, and for the search button —
      // its handler validates the address and keeps this panel open to show any error.
      if (target?.closest(`#${containerId}`) || target?.closest('[data-search-trigger]')) {
        eventDetails.cancel();
        return;
      }
    }

    setIsOpen(next);
    if (!next) handleClose();
  };

  const handleSelectAddress = (address: Address) => {
    hasUserEditedWhereRef.current = true;
    setWhereValue(address.name);
    // Saved addresses carry a book id; Google predictions are geocoded (and validated) by name.
    if (address.isAutocompleteResult) {
      selectTypedAddress(address.name);
    } else {
      selectSavedAddress(address);
    }
  };

  const handleSelectRecentAddress = (recent: RecentAddress) => {
    hasUserEditedWhereRef.current = true;
    setWhereValue(recent.displayValue);
    selectRecentAddress(recent);
  };

  const handleSelectHistory = (entry: EventBarSearchHistoryEntry) => {
    const rank = searchHistory.findIndex(
      historyEntry => historyEntry.fulfillmentDetailId === entry.fulfillmentDetailId,
    );
    trackSearchHistorySelected({
      entry,
      rank: rank + 1,
      resultCount: searchHistory.length,
      source: 'where',
    });
    hasUserEditedWhereRef.current = true;
    setWhereValue(entry.address.fullAddress || entry.addressLabel);
    selectSearchHistoryAddress(searchHistoryAddressToGeocoded(entry.address));
  };

  const focusWhereInput = (index: number) => {
    inputRef.current?.focus();
    setActiveIndex(index);
  };

  const activateOption = (index: number) => {
    // Index 0 is the toggle, which is switched with Left/Right rather than activated here.
    if (index < 1) return;
    const option = options[index - 1];
    if (!option) return;
    if (option.kind === 'geo') triggerGeolocation();
    else if (option.kind === 'recent') handleSelectRecentAddress(option.recent);
    else if (option.kind === 'history') handleSelectHistory(option.entry);
    else handleSelectAddress(option.address);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const lastIndex = optionCount - 1;
    switch (event.key) {
      case 'Tab':
        // While the panel is open, Base UI's popup focus guards have no trigger to hand focus back
        // to, so Tab would dead-end inside the portal. Move to the next field ourselves — and run
        // the close handler, since closing through `setIsOpen` skips Base UI's `onOpenChange`.
        // Only suppress the keystroke if focus actually moved, so a field with no adjacent tabbable
        // falls through to the browser's own Tab rather than stranding focus on a closed panel.
        if (isOpen) {
          setIsOpen(false);
          handleClose();
          if (focusAdjacentTabbable(event.currentTarget, event.shiftKey ? -1 : 1)) {
            event.preventDefault();
          }
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        setIsOpen(true);
        // Slot 0 is the toggle; the account dropdown sits just below it. Moving onto it hands
        // real DOM focus to the Select trigger rather than advancing the roving index.
        if (hasMultipleCorpAccounts && activeIndex === 0) {
          corpSelectTriggerRef.current?.focus();
        } else {
          setActiveIndex(prev => (prev >= lastIndex ? 0 : prev + 1));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        setIsOpen(true);
        // Slot 1 is the first row below the account dropdown; moving up from it lands on the
        // dropdown (real focus), not the toggle.
        if (hasMultipleCorpAccounts && activeIndex === 1) {
          corpSelectTriggerRef.current?.focus();
        } else {
          setActiveIndex(prev => (prev <= 0 ? lastIndex : prev - 1));
        }
        break;
      case 'ArrowRight':
      case 'ArrowLeft':
        if (activeIndex === 0) {
          event.preventDefault();
          const current = FULFILLMENT_OPTIONS.indexOf(fulfillmentType);
          const delta = event.key === 'ArrowRight' ? 1 : -1;
          const next = (current + delta + FULFILLMENT_OPTIONS.length) % FULFILLMENT_OPTIONS.length;
          handleFulfillmentChange(FULFILLMENT_OPTIONS[next]);
        }
        break;
      case 'Home':
        if (isOpen && activeIndex >= 0) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (isOpen && activeIndex >= 0) {
          event.preventDefault();
          setActiveIndex(lastIndex);
        }
        break;
      case 'Enter':
        if (isOpen && activeIndex >= 1) {
          event.preventDefault();
          // activateOption -> handleSelectAddress handles advancing / validation.
          activateOption(activeIndex);
        } else if (activeIndex !== 0 && whereValue.trim().length > 0) {
          // A typed value with nothing highlighted: geocode + validate (may surface incomplete /
          // multiple-match states) instead of blindly advancing.
          event.preventDefault();
          selectTypedAddress(whereValue);
        }
        break;
      default:
        break;
    }
  };

  // Only the single most-critical alert is shown, at the bottom of the panel: the blocking
  // "complete address" validation first, then a geolocation failure, then the informational
  // precise-address hint.
  let activeAlert: {
    kind: 'incompleteAddress' | 'geolocationError' | 'locationHint';
    text: string;
    testId?: string;
    role?: 'alert';
  } | null;

  if (incompleteAddress) {
    activeAlert = {
      kind: 'incompleteAddress',
      text: t('eventBarV2.where.validation.incompleteAddress'),
      testId: 'where-incomplete-address-alert',
      role: 'alert',
    };
  } else if (showUserGeolocatedError) {
    activeAlert = {
      kind: 'geolocationError',
      text: t('eventBarV2.where.useCurrentLocation.error'),
      role: 'alert',
    };
  } else if (showPreciseAddressNudge && showSuggestions) {
    activeAlert = null;
  } else {
    activeAlert = {
      kind: 'locationHint',
      text: showPreciseAddressNudge
        ? t('common:components.AddressSearchAutocomplete.preciseAddressNudge')
        : t('eventBarV2.where.locationAlert'),
    };
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <label
        className={twJoin(
          'relative -left-2 flex flex-col justify-center rounded-full border-2 border-transparent py-2 transition-all duration-200 focus-within:z-10 hover:z-10 motion-reduce:transition-none',
          isCompressed ? 'h-[60px] px-5' : 'h-20 px-8',
        )}
        htmlFor={inputId}
        id={containerId}
        ref={anchorRef}
      >
        {!isCompressed && (
          <span className="flex items-start gap-1 text-nowrap font-semibold text-peppercorn-800">
            {t('eventBarV2.where.label')}
            {showPreciseAddressRedDot && redDot}
          </span>
        )}
        {isCompressed && showPreciseAddressRedDot && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-5 right-5 flex items-center"
          >
            <span className="invisible min-w-0 truncate whitespace-pre font-semibold">
              {whereValue || placeholder}
            </span>
            <span className="visible mb-3 ml-1 flex shrink-0">{redDot}</span>
          </span>
        )}
        <TextField.Root className="border-none bg-transparent p-0 focus-within:ring-0 hover:bg-transparent">
          <TextField.Input
            aria-activedescendant={activeDescendantId}
            aria-autocomplete="list"
            aria-controls={`${fulfillmentRadiogroupId(idSuffix)} ${listboxId}`}
            aria-describedby={showPreciseAddressTextPrompt ? textPromptId : undefined}
            aria-expanded={isOpen}
            autoComplete="off"
            className={twJoin(
              'truncate p-0 focus:outline-none focus:ring-0',
              isCompressed && 'font-semibold placeholder:text-peppercorn-500',
              showPreciseAddressTextPrompt && 'underline decoration-dotted underline-offset-4',
            )}
            data-testid="where-input"
            id={inputId}
            onBlur={() => {
              setActiveIndex(-1);
            }}
            onChange={event => {
              hasUserEditedWhereRef.current = true;
              setHasEditedSinceFocus(true);
              // Reopen the panel when typing resumes after it was dismissed (e.g. via Escape),
              // since focus never left the input so onFocus won't fire again.
              setIsOpen(true);
              setWhereValue(event.target.value);
              setWhereSelection(null);
              clearValidation();
              setActiveIndex(-1);
              // Editing invalidates any prior geolocation result so it can re-fire later.
              clearUserGeolocationResults();
            }}
            // Reopen on click when the input already holds focus (onFocus won't fire again).
            onClick={() => setIsOpen(true)}
            onFocus={event => {
              const inputField = event.currentTarget;

              inputField.setSelectionRange(0, inputField.value.length, 'backward');
              selectOnMouseUpRef.current = true;
              setIsOpen(true);
              setHasEditedSinceFocus(false);
            }}
            onKeyDown={handleKeyDown}
            onMouseUp={event => {
              // Safari/Firefox collapse the focus-triggered select() on mouseup; guard the first one.
              if (selectOnMouseUpRef.current) event.preventDefault();
              selectOnMouseUpRef.current = false;
            }}
            placeholder={placeholder}
            ref={inputRef}
            role="combobox"
            value={whereValue}
          />
        </TextField.Root>
        {showPreciseAddressTextPrompt && (
          <span
            className="text-nowrap text-xs font-semibold text-green-600"
            data-testid="where-precise-address-text-prompt"
            id={textPromptId}
          >
            {t('eventBarV2.where.addStreetAddress')}
          </span>
        )}
      </label>
      <Popover.Content
        align="start"
        anchor={anchorRef}
        className="mt-2 flex max-h-[min(600px,var(--available-height))] w-[460px] flex-col overflow-hidden rounded-2xl border border-peppercorn-100 bg-white shadow-lg"
        data-event-bar-surface
        data-testid="where-suggestions-panel"
        finalFocus={false}
        initialFocus={false}
        sideOffset={8}
      >
        {/* Scrollable region: the toggle, account select, and address list scroll; the alert and
            Update button below stay pinned. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6" data-testid="where-suggestions-scroll">
          <div className="mb-3">
            <DeliveryPickupToggle
              idSuffix={idSuffix}
              isActive={activeOptionIndex === 0}
              onChange={handleFulfillmentChange}
              value={fulfillmentType}
            />
          </div>

          {hasMultipleCorpAccounts && (
            <WhereCorpAccountSelect
              onDropdownClose={() => focusWhereInput(-1)}
              onNavigateOut={direction =>
                focusWhereInput(direction === 'up' ? 0 : Math.min(1, optionCount - 1))
              }
              triggerRef={corpSelectTriggerRef}
            />
          )}

          {addressesToVerify ? (
            <>
              <Alert className="mb-3" data-testid="where-multiple-matches-alert" variant="critical">
                <AlertTitle className="text-base font-normal">
                  {t('eventBarV2.where.validation.multipleMatches')}
                </AlertTitle>
              </Alert>
              <div
                aria-label={t('eventBarV2.where.validation.multipleMatches')}
                className="flex flex-col gap-2"
                data-testid="where-address-candidates"
                role="group"
              >
                {addressesToVerify.map((candidate, index) => {
                  const label = geocodedAddressResultToString(candidate);
                  return (
                    <button
                      className="w-full cursor-pointer rounded-lg border border-peppercorn-200 px-4 py-3 text-left text-sm text-peppercorn-800 hover:bg-peppercorn-50"
                      data-testid="where-address-candidate"
                      key={`${label}-${index}`}
                      onClick={() => {
                        setWhereValue(label);
                        selectGeocoded(candidate, true, label);
                      }}
                      onMouseDown={event => event.preventDefault()}
                      type="button"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div
                aria-label={t('eventBarV2.where.label')}
                className="flex flex-col gap-1"
                id={listboxId}
                role="listbox"
              >
                <UseCurrentLocationRow
                  id={optionId(GEO_OPTION_INDEX, idSuffix)}
                  isActive={activeOptionIndex === GEO_OPTION_INDEX}
                  isLoading={isLoading}
                  onClick={triggerGeolocation}
                />

                {savedAddresses.length > 0 && (
                  <div aria-label={t('eventBarV2.where.savedAddressLabel')} role="group">
                    <div
                      aria-hidden
                      className="px-2 pt-2 text-xs font-semibold text-peppercorn-500"
                    >
                      {t('eventBarV2.where.savedAddressLabel')}
                    </div>
                    {savedAddresses.map((address, index) => (
                      <WhereAddressOption
                        address={address}
                        id={optionId(savedStart + index, idSuffix)}
                        isActive={activeOptionIndex === savedStart + index}
                        key={address.placeId}
                        onClick={() => handleSelectAddress(address)}
                        searchTerm={searchQuery}
                      />
                    ))}
                  </div>
                )}

                {showRecentAddresses && (
                  <div aria-label={t('eventBarV2.where.recentlyUsedLabel')} role="group">
                    <div
                      aria-hidden
                      className="px-2 pt-2 text-xs font-semibold text-peppercorn-500"
                    >
                      {t('eventBarV2.where.recentlyUsedLabel')}
                    </div>
                    {filteredRecentAddresses.map((recent, index) => (
                      <WhereAddressOption
                        address={recentAddressToDisplayAddress(recent)}
                        id={optionId(recentStart + index, idSuffix)}
                        isActive={activeOptionIndex === recentStart + index}
                        key={recent.displayValue}
                        onClick={() => handleSelectRecentAddress(recent)}
                        searchTerm={searchQuery}
                      />
                    ))}
                  </div>
                )}

                {showSearchHistory && (
                  <div aria-label={t('eventBarV2.where.recentSearchesLabel')} role="group">
                    <div
                      aria-hidden
                      className="px-2 pt-2 text-xs font-semibold text-peppercorn-500"
                    >
                      {t('eventBarV2.where.recentSearchesLabel')}
                    </div>
                    {searchHistory.map((entry, index) => (
                      <EventBarSearchHistoryOption
                        entry={entry}
                        id={optionId(searchHistoryStart + index, idSuffix)}
                        isActive={activeOptionIndex === searchHistoryStart + index}
                        key={entry.fulfillmentDetailId}
                        onSelect={() => handleSelectHistory(entry)}
                        showSummary={false}
                      />
                    ))}
                  </div>
                )}

                {visiblePredictions.length > 0 && (
                  <div aria-label={t('eventBarV2.where.suggestionsLabel')} role="group">
                    <div
                      aria-hidden
                      className="px-2 pt-2 text-xs font-semibold text-peppercorn-500"
                    >
                      {t('eventBarV2.where.suggestionsLabel')}
                    </div>
                    {visiblePredictions.map((address, index) => (
                      <WhereAddressOption
                        address={address}
                        id={optionId(predictionsStart + index, idSuffix)}
                        isActive={activeOptionIndex === predictionsStart + index}
                        key={address.placeId}
                        onClick={() => handleSelectAddress(address)}
                        searchTerm={searchQuery}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Pinned footer: the Google attribution, the most-critical alert, and the Update button all
            stay out of the scroll. */}
        {(!addressesToVerify || isOnMenuPage) && (
          <div className="flex flex-col gap-4 px-6 pb-6">
            {!addressesToVerify && visiblePredictions.length > 0 && (
              <div className="flex justify-end">
                <Image
                  alt={t('common:components.AddressSearchAutocomplete.poweredByGoogle')}
                  height="15"
                  src={poweredByGoogleImage.src}
                  width="120"
                />
              </div>
            )}

            {!addressesToVerify && activeAlert && (
              <InfoBanner
                data-testid={activeAlert.testId}
                key={activeAlert.kind}
                role={activeAlert.role}
                text={activeAlert.text}
                variant={showRedDot ? 'warning' : 'info'}
              />
            )}

            {isOnMenuPage && (
              <Button
                className="w-full"
                data-testid="where-update-button"
                loading={isUpdating}
                onClick={handleUpdate}
              >
                {t('eventBarV2.update')}
              </Button>
            )}
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

export default Where;
